import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  CalendarDays, Users, Clock, TrendingUp, Download, ChevronLeft, Globe,
  LogIn, LogOut, Coffee, AlertCircle, ShieldAlert, UserCheck, ChevronRight,
  Timer, Layers
} from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const STATUS_COLORS = {
  present: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  absent: 'bg-red-100 text-red-700 border-red-200',
  late: 'bg-amber-100 text-amber-700 border-amber-200',
  half_day: 'bg-blue-100 text-blue-700 border-blue-200',
  on_leave: 'bg-purple-100 text-purple-700 border-purple-200',
  day_off: 'bg-gray-100 text-gray-600 border-gray-200',
  not_marked: 'bg-gray-100 text-gray-500 border-gray-200',
};

function fmtDur(minutes) {
  if (!minutes && minutes !== 0) return '–';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function DailyReportPage() {
  const navigate = useNavigate();
  const { fromDate, setFromDate, toDate, setToDate, selectedStaff, setSelectedStaff,
    staffList, timezone, setPreset, formatTZ, formatHours } = useReportFilters();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionsModal, setSessionsModal] = useState(null); // { record }
  const [breaksModal, setBreaksModal] = useState(null);     // { record }

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports/daily-detailed`, {
        params: { from_date: fromDate, to_date: toDate, staff_id: selectedStaff === 'all' ? undefined : selectedStaff },
        withCredentials: true
      });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, [fromDate, toDate, selectedStaff]);

  const chartData = (() => {
    if (!data?.records?.length) return [];
    const byDate = {};
    data.records.forEach(r => {
      const d = r.date || '';
      if (!byDate[d]) byDate[d] = { date: d, present: 0, absent: 0, late: 0, on_leave: 0 };
      const s = r.status || 'not_marked';
      if (s === 'present') byDate[d].present++;
      else if (s === 'late') byDate[d].late++;
      else if (s === 'on_leave' || s === 'leave' || s === 'holiday') byDate[d].on_leave++;
      else byDate[d].absent++;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  const stats = data?.records ? {
    total: data.records.length,
    present: data.records.filter(r => r.status === 'present').length,
    late: data.records.filter(r => r.status === 'late').length,
    absent: data.records.filter(r => ['absent', 'not_marked'].includes(r.status)).length,
    forceCheckouts: data.records.filter(r => r.force_checkout).length,
  } : null;

  const handleCSV = () => {
    const csv = ['Date,Staff,Status,Check-in,Check-out,Sessions,Break Hrs,Breaks,Force Breaks,Working Hrs,Productive Hrs,Idle Hrs,Idle L1 Hrs,Idle L1 Count,Idle L2 Hrs,Idle L2 Count,Idle L3 Hrs,Idle L3 Count,Overtime,Late By,Force Checkout']
      .concat((data?.records || []).map(r =>
        `${r.date},${r.staff_name},${r.status},${formatTZ(r.check_in) || ''},${formatTZ(r.check_out) || ''},${r.sessions_count},${fmtDur(r.total_break_minutes)},${r.break_count},${r.force_break_count || 0},${formatHours(r.working_hours || r.total_working_minutes / 60)},${formatHours(r.productive_hours ?? Math.max(0, (r.working_hours || r.total_working_minutes / 60) - (r.idle_minutes || 0) / 60))},${fmtDur(r.idle_minutes)},${(r.idle_level_1_minutes||0).toFixed(1)},${r.idle_level_1_count||0},${(r.idle_level_2_minutes||0).toFixed(1)},${r.idle_level_2_count||0},${(r.idle_level_3_minutes||0).toFixed(1)},${r.idle_level_3_count||0},${fmtDur(r.overtime_minutes)},${r.late_by > 0 ? r.late_by + 'm' : ''},${r.force_checkout ? 'Yes' : 'No'}`
      )).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `daily-report-${fromDate}-${toDate}.csv`;
    a.click();
  };

  return (
    <div className="relative pb-8" data-testid="daily-report-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
      <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate('/admin/reports')}
                className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Daily Report</h1>
                <div className="mt-1.5 flex items-center gap-1.5 text-sm text-indigo-50/90">
                  <Globe className="h-3.5 w-3.5" />
                  <span>Times shown in {timezone}</span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleCSV}
              className="h-11 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="att-fade-up overflow-hidden rounded-3xl border border-slate-200/70 bg-white p-4 shadow-sm sm:p-5" style={{ animationDelay: '60ms' }}>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Preset</Label>
              <div className="flex flex-wrap gap-1.5">
                {[['today','Today'],['week','This Week'],['month','This Month'],['last_month','Last Month']].map(([k,l]) => (
                  <Button key={k} size="sm" variant="outline" onClick={() => setPreset(k)} className="h-9 rounded-xl text-xs">{l}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">From</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 w-36 rounded-xl text-sm" />
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">To</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 w-36 rounded-xl text-sm" />
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Staff</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="h-9 w-44 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
      </div>

      {loading && <div className="py-16 text-center text-slate-400">Loading report...</div>}

      {!loading && data && (
        <>
          {/* Summary Stats */}
          <div className="att-fade-up grid grid-cols-2 gap-3 lg:grid-cols-5" style={{ animationDelay: '100ms' }}>
            {[
              { label: 'Total Records', value: stats.total, soft: 'bg-slate-100 text-slate-600', accent: 'from-slate-400 to-slate-600', Icon: Users },
              { label: 'Present', value: stats.present, soft: 'bg-indigo-50 text-indigo-600', accent: 'from-indigo-500 to-purple-500', Icon: TrendingUp },
              { label: 'Late', value: stats.late, soft: 'bg-amber-50 text-amber-700', accent: 'from-amber-500 to-orange-500', Icon: Clock },
              { label: 'Absent', value: stats.absent, soft: 'bg-rose-50 text-rose-600', accent: 'from-rose-500 to-pink-500', Icon: CalendarDays },
              { label: 'Force Checkouts', value: stats.forceCheckouts, soft: 'bg-orange-50 text-orange-600', accent: 'from-orange-500 to-amber-500', Icon: ShieldAlert },
            ].map(({ label, value, soft, accent, Icon }) => (
              <div key={label} className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
                <div className={`absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br ${accent} opacity-10 blur-xl`} />
                <div className="relative flex items-center gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${soft}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xl font-bold tabular-nums text-slate-900">{value}</p>
                    <p className="text-[11px] font-medium text-slate-500 leading-tight">{label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Attendance Trend Chart */}
          {chartData.length > 1 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Attendance Trend</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" fill="#22c55e" name="Present" radius={[3,3,0,0]} />
                    <Bar dataKey="late" fill="#f59e0b" name="Late" radius={[3,3,0,0]} />
                    <Bar dataKey="absent" fill="#ef4444" name="Absent" radius={[3,3,0,0]} />
                    <Bar dataKey="on_leave" fill="#a855f7" name="On Leave" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Attendance Records Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-blue-500" />
                  Attendance Records
                  <span className="text-sm font-normal text-gray-400">({data.records?.length})</span>
                </CardTitle>
                <Button size="sm" variant="outline" onClick={handleCSV} data-testid="download-csv-btn">
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {['Date', 'Staff', 'Status', 'Check-in', 'Check-out', 'Sessions', 'Break Hrs', 'Working Hrs', 'Productive HRS', 'Idle', 'Overtime', 'Flags'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(data.records || []).map((r, i) => {
                      const wh = r.working_hours ?? (r.total_working_minutes / 60);
                      return (
                        <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                          {/* Date */}
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-sm">{r.date}</td>

                          {/* Staff */}
                          <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{r.staff_name}</td>

                          {/* Status */}
                          <td className="px-4 py-3">
                            <Badge className={`text-xs border ${STATUS_COLORS[r.status] || STATUS_COLORS.not_marked}`}>
                              {(r.status || 'not marked').replace(/_/g, ' ')}
                            </Badge>
                            {r.late_by > 0 && (
                              <span className="ml-1.5 text-xs text-amber-600 font-medium">+{r.late_by}m late</span>
                            )}
                          </td>

                          {/* Check-in */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.check_in ? (
                              <div className="flex items-center gap-1.5">
                                <LogIn className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                <span className="font-mono text-xs text-gray-700">{formatTZ(r.check_in)}</span>
                              </div>
                            ) : <span className="text-gray-300 text-xs">–</span>}
                          </td>

                          {/* Check-out */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.check_out ? (
                              <div className="flex items-center gap-1.5">
                                <LogOut className="w-3.5 h-3.5 text-red-400 shrink-0" />
                                <span className="font-mono text-xs text-gray-700">{formatTZ(r.check_out)}</span>
                              </div>
                            ) : <span className="text-gray-300 text-xs">–</span>}
                          </td>

                          {/* Sessions — clickable */}
                          <td className="px-4 py-3">
                            {r.sessions_count > 0 ? (
                              <button
                                onClick={() => setSessionsModal(r)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors group"
                                data-testid={`sessions-btn-${i}`}
                              >
                                <Layers className="w-3.5 h-3.5" />
                                <span className="text-xs font-semibold">{r.sessions_count}</span>
                                <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </button>
                            ) : (
                              <span className="text-gray-300 text-xs">–</span>
                            )}
                          </td>

                          {/* Break Hours */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.total_break_minutes > 0 ? (
                              <button
                                onClick={() => setBreaksModal(r)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors group"
                                data-testid={`breaks-btn-${i}`}
                              >
                                <Coffee className="w-3.5 h-3.5" />
                                <span className="text-xs font-semibold">{fmtDur(r.total_break_minutes)}</span>
                                {r.break_count > 0 && <span className="text-[10px] text-amber-500">×{r.break_count}</span>}
                                {r.force_break_count > 0 && <span className="text-[10px] bg-orange-200 text-orange-700 px-1 rounded">{r.force_break_count}F</span>}
                                <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </button>
                            ) : (
                              <span className="text-gray-300 text-xs">–</span>
                            )}
                          </td>

                          {/* Working Hours */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {wh > 0 ? (
                              <div className="flex items-center gap-1.5">
                                <Timer className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                <span className="font-semibold text-blue-600">{formatHours(wh)}</span>
                              </div>
                            ) : <span className="text-gray-300 text-xs">–</span>}
                          </td>

                          {/* Productive HRS = Working - Idle */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              const productive = r.productive_hours ?? Math.max(0, wh - (r.idle_minutes || 0) / 60);
                              return productive > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <Timer className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                                  <span className="font-semibold text-indigo-600">{formatHours(productive)}</span>
                                </div>
                              ) : <span className="text-gray-300 text-xs">–</span>;
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            {r.idle_minutes > 0 ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs font-semibold text-red-500">{fmtDur(r.idle_minutes)}</span>
                                <div className="flex flex-wrap gap-0.5">
                                  {r.idle_level_1_minutes > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">
                                      L1 {fmtDur(r.idle_level_1_minutes)}{r.idle_level_1_count > 1 ? ` ×${r.idle_level_1_count}` : ''}
                                    </span>
                                  )}
                                  {r.idle_level_2_minutes > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">
                                      L2 {fmtDur(r.idle_level_2_minutes)}{r.idle_level_2_count > 1 ? ` ×${r.idle_level_2_count}` : ''}
                                    </span>
                                  )}
                                  {r.idle_level_3_minutes > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                                      L3 {fmtDur(r.idle_level_3_minutes)}{r.idle_level_3_count > 1 ? ` ×${r.idle_level_3_count}` : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : <span className="text-gray-300 text-xs">–</span>}
                          </td>

                          {/* Overtime */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.overtime_minutes > 0 ? (
                              <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded-md">
                                +{fmtDur(r.overtime_minutes)}
                              </span>
                            ) : <span className="text-gray-300 text-xs">–</span>}
                          </td>

                          {/* Flags */}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1 min-w-[80px]">
                              {!r.check_out && r.check_in && (
                                <span className="flex items-center gap-0.5 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200 whitespace-nowrap" data-testid={`flag-nocheckout-${i}`}>
                                  <Clock className="w-3 h-3" /> No Checkout
                                </span>
                              )}
                              {r.force_checkout && (
                                <span className="flex items-center gap-0.5 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded border border-red-200 whitespace-nowrap" title="Force Checkout" data-testid={`flag-force-${i}`}>
                                  <ShieldAlert className="w-3 h-3" /> Force Out
                                </span>
                              )}
                              {r.marked_by_admin && (
                                <span className="flex items-center gap-0.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 whitespace-nowrap" title="Marked by Admin" data-testid={`flag-admin-${i}`}>
                                  <UserCheck className="w-3 h-3" /> Admin
                                </span>
                              )}
                              {r.force_break_count > 0 && (
                                <span className="flex items-center gap-0.5 text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded border border-orange-200 whitespace-nowrap" data-testid={`flag-forcebreak-${i}`}>
                                  <Coffee className="w-3 h-3" /> F.Break
                                </span>
                              )}
                              {!r.check_out && !r.check_in && (
                                <span className="text-gray-300 text-xs">–</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(!data.records || data.records.length === 0) && (
                  <div className="text-center py-12 text-gray-400">No records for this period</div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!loading && !data && (
        <div className="text-center py-16 text-gray-400">No data found for selected period</div>
      )}

      {/* ===== Sessions Modal ===== */}
      <Dialog open={!!sessionsModal} onOpenChange={(open) => { if (!open) setSessionsModal(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" />
              Sessions — {sessionsModal?.staff_name}
              <span className="text-sm font-normal text-gray-500 ml-1">{sessionsModal?.date}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="text-xs text-gray-500 mb-3">
            {sessionsModal?.sessions_count} session{sessionsModal?.sessions_count !== 1 ? 's' : ''} recorded &nbsp;·&nbsp;
            Total working: <span className="font-semibold text-blue-600">{formatHours(sessionsModal?.working_hours || (sessionsModal?.total_working_minutes / 60))}</span>
          </div>

          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {(sessionsModal?.sessions_detail || []).map((s, idx) => (
              <div
                key={idx}
                className={`rounded-xl border p-4 ${s.force_checkout ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800">
                    Session {s.session_number}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {s.force_checkout && (
                      <span className="flex items-center gap-0.5 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200">
                        <ShieldAlert className="w-3 h-3" /> Force Checkout
                      </span>
                    )}
                    {s.marked_by_admin && (
                      <span className="flex items-center gap-0.5 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                        <UserCheck className="w-3 h-3" /> Admin Marked
                      </span>
                    )}
                    <Badge className={`text-xs ${STATUS_COLORS[s.status] || STATUS_COLORS.not_marked}`}>
                      {(s.status || '').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                      <LogIn className="w-3.5 h-3.5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">Check-in</p>
                      <p className="text-xs font-mono font-semibold text-gray-800">{formatTZ(s.check_in) || '–'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${s.force_checkout ? 'bg-orange-100' : 'bg-red-100'}`}>
                      <LogOut className={`w-3.5 h-3.5 ${s.force_checkout ? 'text-orange-600' : 'text-red-500'}`} />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">
                        {s.force_checkout ? 'Force Checkout' : 'Check-out'}
                      </p>
                      <p className="text-xs font-mono font-semibold text-gray-800">{formatTZ(s.check_out) || '–'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Timer className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">Duration</p>
                      <p className="text-xs font-semibold text-blue-700">{fmtDur(s.duration_minutes)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(!sessionsModal?.sessions_detail?.length) && (
              <p className="text-center text-sm text-gray-400 py-6">No session details available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Breaks Modal ===== */}
      <Dialog open={!!breaksModal} onOpenChange={(open) => { if (!open) setBreaksModal(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="w-5 h-5 text-amber-500" />
              Break Details — {breaksModal?.staff_name}
              <span className="text-sm font-normal text-gray-500 ml-1">{breaksModal?.date}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
              <p className="text-lg font-bold text-amber-700">{breaksModal?.break_count || 0}</p>
              <p className="text-xs text-gray-500">Total Breaks</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-3 text-center border border-orange-100">
              <p className="text-lg font-bold text-orange-700">{breaksModal?.force_break_count || 0}</p>
              <p className="text-xs text-gray-500">Force Breaks</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
              <p className="text-lg font-bold text-blue-700">{fmtDur(breaksModal?.total_break_minutes)}</p>
              <p className="text-xs text-gray-500">Total Break Time</p>
            </div>
          </div>

          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            {(breaksModal?.breaks_detail || []).map((brk, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-4 p-3 rounded-lg border ${brk.is_force_break ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${brk.is_force_break ? 'bg-orange-200' : 'bg-amber-100'}`}>
                  {brk.is_force_break
                    ? <AlertCircle className="w-3.5 h-3.5 text-orange-700" />
                    : <Coffee className="w-3.5 h-3.5 text-amber-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700">Break {idx + 1}</span>
                    {brk.is_force_break && (
                      <span className="text-[10px] bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded font-medium">
                        {brk.break_type === 'manual_force' ? 'Admin Force Break' : 'Force Break'}
                      </span>
                    )}
                    {brk.reason && <span className="text-xs text-gray-400 truncate">· {brk.reason}</span>}
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-gray-500 font-mono">
                    {brk.start_time && <span><LogIn className="w-3 h-3 inline mr-0.5 text-indigo-500" />{formatTZ(brk.start_time)}</span>}
                    {brk.end_time && <span><LogOut className="w-3 h-3 inline mr-0.5 text-red-400" />{formatTZ(brk.end_time)}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-amber-700">{fmtDur(brk.duration_minutes)}</p>
                  <p className="text-[10px] text-gray-400">duration</p>
                </div>
              </div>
            ))}

            {(!breaksModal?.breaks_detail?.length) && (
              <div className="text-center py-8 text-gray-400">
                <Coffee className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No break details available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
